import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getLocalItem } from '../../utils/storage';

// Booking Decision Gate — full-screen blocking YES/NO modal for an open booking
// offer. Mounted at app level (src/App.js) on every /mechanic-dashboard* page.
//
// Server-driven: it is on screen only while GET pending_decisions says an offer
// is open, or while finishing one of the short closing states below.
// FAIL-OPEN, NEVER TRAP: any poll error hides it; every state is time-bounded.
//
// SSR: nothing here touches window/document at module load or during a server
// render. It renders null on the server and until componentDidMount.

const API = 'https://ridenrepair.com';

const POLL_IDLE_MS = 10000;          // no offer open
const POLL_OPEN_MS = 3000;           // offer open
const POLL_TIMEOUT_MS = 8000;
const SEND_TIMEOUT_MS = 15000;
const REASON_TIMEOUT_MS = 8000;
const TICK_MS = 250;
const WAKE_THROTTLE_MS = 1000;       // focus + visibilitychange usually fire together
const POLL_FAILS_BEFORE_HIDE = 2;    // fail-open, but one dropped poll on mobile data must not flicker a 60s offer
const ERROR_OFFLINE_CLOSE_MS = 60000; // ERROR + no successful poll this long -> hide
const MAX_SERVER_FAILS = 3;          // HTTP-answered send failures per offer before giving up
const GRACE_VERIFY_MS = 5000;        // after call grace: wait this long for one fresh poll
const VANISH_CONFIRM_POLLS = 2;      // the server answers an internal error with an empty list
const ARM_MS = 500;                  // ignore taps right after buttons appear (stray taps)
const DEFAULT_CALLER_ID = '01205232888';
const DEFAULT_CALL_GRACE_MS = 30000;
const IST_OFFSET_MS = 19800000;
const TRAP_KEY = '__rnrDecisionGate';

const OPEN = 'OPEN';
const CALLING = 'CALLING';
const MISSED = 'MISSED';
const SUBMITTING = 'SUBMITTING';
const ACCEPTED = 'ACCEPTED';
const GOING = 'GOING';
const REASON = 'REASON';
const GONE = 'GONE';
const ERROR = 'ERROR';

const AUTO_CLOSE_MS = { MISSED: 6000, ACCEPTED: 6000, GOING: 4000, GONE: 5000, REASON: 30000 };

const CLEARED = {
  current: null, phase: null, total: 0, pendingDecision: null,
  reasonSending: false, neutral: false, vanished: false,
};

const REASONS = {
  accept: ['not_available', 'on_leave_today', 'other'],
  going: ['far_from_me', 'service_type_different', 'time_not_preferred', 'other'],
};

const STR = {
  hi: {
    titleAccept: 'नई बुकिंग — क्या आप लेंगे?',
    titleGoing: 'क्या आप कस्टमर के पास जा रहे हैं?',
    yes: 'हाँ',
    no: 'नहीं',
    going: 'जा रहा हूँ',
    notGoing: 'नहीं जा रहा',
    timeLeft: 'बचा समय',
    calling: 'समय खत्म — हम आपको अभी कॉल कर रहे हैं। यह बुकिंग रखने के लिए {num} से आने वाला कॉल उठाएँ।',
    missed: 'यह बुकिंग छूट गई — आपने जवाब नहीं दिया।',
    submitting: 'भेज रहे हैं…',
    accepted: 'बुकिंग आपकी है। हम आपको अभी कॉल कर रहे हैं — {num} से आने वाला कॉल उठाएँ।',
    goingOk: 'बढ़िया — बुकिंग In Progress हो गई। संभलकर जाएँ।',
    reasonTitle: 'क्यों नहीं?',
    r_not_available: 'अभी खाली नहीं हूँ',
    r_on_leave_today: 'आज छुट्टी पर हूँ',
    r_other: 'कोई और वजह',
    r_far_from_me: 'बहुत दूर है',
    r_service_type_different: 'यह सर्विस मैं नहीं करता',
    r_time_not_preferred: 'यह टाइम ठीक नहीं',
    gone: 'यह बुकिंग अब नहीं है — दूसरे मैकेनिक को चली गई।',
    closed: 'यह ऑफर बंद हो गया। अपनी बुकिंग देखें।',
    error: 'भेज नहीं पाए — नेटवर्क देखें',
    retry: 'फिर से भेजें',
    ok: 'ठीक है',
    queue: '{n} में से 1',
    lblVehicle: 'गाड़ी',
    lblService: 'सर्विस',
    lblArea: 'एरिया',
    lblTime: 'समय',
    today: 'आज',
    tomorrow: 'कल',
  },
  en: {
    titleAccept: 'New booking — will you take it?',
    titleGoing: 'Are you going to the customer?',
    yes: 'YES',
    no: 'NO',
    going: 'Going',
    notGoing: 'Not going',
    timeLeft: 'Time left',
    calling: 'Time is up — we are calling you now. Pick up the call from {num} to keep this booking.',
    missed: 'You missed this booking — you did not respond.',
    submitting: 'Sending…',
    accepted: 'Booking is yours. We are calling you now — pick up the call from {num}.',
    goingOk: 'Great — booking is In Progress. Drive safe.',
    reasonTitle: 'Why not?',
    r_not_available: 'Not free right now',
    r_on_leave_today: 'On leave today',
    r_other: 'Other reason',
    r_far_from_me: 'Too far from me',
    r_service_type_different: 'I do not do this service',
    r_time_not_preferred: 'This time does not suit me',
    gone: 'This booking is no longer available — it went to another mechanic.',
    closed: 'This offer is closed. Check your bookings.',
    error: 'Could not send — check network',
    retry: 'Retry',
    ok: 'OK',
    queue: '1 of {n}',
    lblVehicle: 'Vehicle',
    lblService: 'Service',
    lblArea: 'Area',
    lblTime: 'Time',
    today: 'Today',
    tomorrow: 'Tomorrow',
  },
  ta: {
    titleAccept: 'புதிய புக்கிங் — எடுக்கிறீங்களா?',
    titleGoing: 'கஸ்டமர்கிட்ட போறீங்களா?',
    yes: 'ஆம்',
    no: 'வேண்டாம்',
    going: 'போறேன்',
    notGoing: 'போகலை',
    timeLeft: 'மீதி நேரம்',
    calling: 'நேரம் முடிஞ்சது — இப்போ உங்களுக்கு கால் பண்றோம். இந்த புக்கிங் வேணும்னா {num} நம்பர்ல இருந்து வர்ற காலை எடுங்க.',
    missed: 'இந்த புக்கிங் போயிடுச்சு — நீங்க பதில் சொல்லலை.',
    submitting: 'அனுப்புறோம்…',
    accepted: 'புக்கிங் உங்களுடையது. இப்போ கால் பண்றோம் — {num} நம்பர்ல இருந்து வர்ற காலை எடுங்க.',
    goingOk: 'சூப்பர் — புக்கிங் In Progress ஆயிடுச்சு. பத்திரமா போங்க.',
    reasonTitle: 'ஏன் வேண்டாம்?',
    r_not_available: 'இப்போ ஃப்ரீ இல்லை',
    r_on_leave_today: 'இன்னைக்கு லீவு',
    r_other: 'வேற காரணம்',
    r_far_from_me: 'ரொம்ப தூரம்',
    r_service_type_different: 'இந்த சர்வீஸ் நான் பண்றதில்லை',
    r_time_not_preferred: 'இந்த நேரம் சரிவராது',
    gone: 'இந்த புக்கிங் இப்போ இல்லை — வேற மெக்கானிக்குக்கு போயிடுச்சு.',
    closed: 'இந்த ஆஃபர் முடிஞ்சிடுச்சு. உங்க புக்கிங்ஸ் பாருங்க.',
    error: 'அனுப்ப முடியலை — நெட்வொர்க் பாருங்க',
    retry: 'மறுபடி அனுப்பு',
    ok: 'சரி',
    queue: '{n}-ல் 1',
    lblVehicle: 'வண்டி',
    lblService: 'சர்வீஸ்',
    lblArea: 'ஏரியா',
    lblTime: 'நேரம்',
    today: 'இன்று',
    tomorrow: 'நாளை',
  },
  te: {
    titleAccept: 'కొత్త బుకింగ్ — తీసుకుంటారా?',
    titleGoing: 'కస్టమర్ దగ్గరికి వెళ్తున్నారా?',
    yes: 'అవును',
    no: 'వద్దు',
    going: 'వెళ్తున్నా',
    notGoing: 'వెళ్ళడం లేదు',
    timeLeft: 'మిగిలిన సమయం',
    calling: 'సమయం అయిపోయింది — ఇప్పుడే మీకు కాల్ చేస్తున్నాం. ఈ బుకింగ్ కావాలంటే {num} నుంచి వచ్చే కాల్ ఎత్తండి.',
    missed: 'ఈ బుకింగ్ మిస్ అయింది — మీరు జవాబు ఇవ్వలేదు.',
    submitting: 'పంపుతున్నాం…',
    accepted: 'బుకింగ్ మీదే. ఇప్పుడే కాల్ చేస్తున్నాం — {num} నుంచి వచ్చే కాల్ ఎత్తండి.',
    goingOk: 'సూపర్ — బుకింగ్ In Progress అయింది. జాగ్రత్తగా వెళ్ళండి.',
    reasonTitle: 'ఎందుకు వద్దు?',
    r_not_available: 'ఇప్పుడు ఖాళీ లేదు',
    r_on_leave_today: 'ఈ రోజు సెలవు',
    r_other: 'వేరే కారణం',
    r_far_from_me: 'చాలా దూరం',
    r_service_type_different: 'ఈ సర్వీస్ నేను చేయను',
    r_time_not_preferred: 'ఈ టైమ్ కుదరదు',
    gone: 'ఈ బుకింగ్ ఇప్పుడు లేదు — వేరే మెకానిక్‌కి వెళ్ళింది.',
    closed: 'ఈ ఆఫర్ ముగిసింది. మీ బుకింగ్స్ చూడండి.',
    error: 'పంపలేకపోయాం — నెట్‌వర్క్ చూడండి',
    retry: 'మళ్ళీ పంపండి',
    ok: 'సరే',
    queue: '{n} లో 1',
    lblVehicle: 'బండి',
    lblService: 'సర్వీస్',
    lblArea: 'ఏరియా',
    lblTime: 'సమయం',
    today: 'ఈ రోజు',
    tomorrow: 'రేపు',
  },
  kn: {
    titleAccept: 'ಹೊಸ ಬುಕಿಂಗ್ — ತಗೊಳ್ತೀರಾ?',
    titleGoing: 'ಕಸ್ಟಮರ್ ಹತ್ತಿರ ಹೋಗ್ತಿದ್ದೀರಾ?',
    yes: 'ಹೌದು',
    no: 'ಬೇಡ',
    going: 'ಹೋಗ್ತಿದ್ದೀನಿ',
    notGoing: 'ಹೋಗ್ತಿಲ್ಲ',
    timeLeft: 'ಉಳಿದ ಸಮಯ',
    calling: 'ಸಮಯ ಮುಗಿದಿದೆ — ಈಗಲೇ ನಿಮಗೆ ಕಾಲ್ ಮಾಡ್ತಿದ್ದೀವಿ. ಈ ಬುಕಿಂಗ್ ಬೇಕಿದ್ರೆ {num} ಇಂದ ಬರುವ ಕಾಲ್ ಎತ್ತಿ.',
    missed: 'ಈ ಬುಕಿಂಗ್ ಮಿಸ್ ಆಯ್ತು — ನೀವು ಉತ್ತರ ಕೊಡಲಿಲ್ಲ.',
    submitting: 'ಕಳಿಸ್ತಿದ್ದೀವಿ…',
    accepted: 'ಬುಕಿಂಗ್ ನಿಮ್ಮದು. ಈಗಲೇ ಕಾಲ್ ಮಾಡ್ತಿದ್ದೀವಿ — {num} ಇಂದ ಬರುವ ಕಾಲ್ ಎತ್ತಿ.',
    goingOk: 'ಸೂಪರ್ — ಬುಕಿಂಗ್ In Progress ಆಗಿದೆ. ಹುಷಾರಾಗಿ ಹೋಗಿ.',
    reasonTitle: 'ಯಾಕೆ ಬೇಡ?',
    r_not_available: 'ಈಗ ಫ್ರೀ ಇಲ್ಲ',
    r_on_leave_today: 'ಇವತ್ತು ರಜೆ',
    r_other: 'ಬೇರೆ ಕಾರಣ',
    r_far_from_me: 'ತುಂಬಾ ದೂರ',
    r_service_type_different: 'ಈ ಸರ್ವೀಸ್ ನಾನು ಮಾಡಲ್ಲ',
    r_time_not_preferred: 'ಈ ಟೈಮ್ ಆಗಲ್ಲ',
    gone: 'ಈ ಬುಕಿಂಗ್ ಈಗ ಇಲ್ಲ — ಬೇರೆ ಮೆಕ್ಯಾನಿಕ್‌ಗೆ ಹೋಯ್ತು.',
    closed: 'ಈ ಆಫರ್ ಮುಗಿದಿದೆ. ನಿಮ್ಮ ಬುಕಿಂಗ್ಸ್ ನೋಡಿ.',
    error: 'ಕಳಿಸಲು ಆಗಲಿಲ್ಲ — ನೆಟ್‌ವರ್ಕ್ ನೋಡಿ',
    retry: 'ಮತ್ತೆ ಕಳಿಸಿ',
    ok: 'ಸರಿ',
    queue: '{n} ರಲ್ಲಿ 1',
    lblVehicle: 'ಗಾಡಿ',
    lblService: 'ಸರ್ವೀಸ್',
    lblArea: 'ಏರಿಯಾ',
    lblTime: 'ಸಮಯ',
    today: 'ಇವತ್ತು',
    tomorrow: 'ನಾಳೆ',
  },
  mr: {
    titleAccept: 'नवीन बुकिंग — घेणार का?',
    titleGoing: 'कस्टमरकडे जात आहात का?',
    yes: 'हो',
    no: 'नको',
    going: 'जातोय',
    notGoing: 'जात नाही',
    timeLeft: 'उरलेला वेळ',
    calling: 'वेळ संपली — आम्ही आत्ता तुम्हाला कॉल करतोय. ही बुकिंग हवी असेल तर {num} वरून येणारा कॉल उचला.',
    missed: 'ही बुकिंग गेली — तुम्ही उत्तर दिलं नाही.',
    submitting: 'पाठवतोय…',
    accepted: 'बुकिंग तुमची आहे. आम्ही आत्ता कॉल करतोय — {num} वरून येणारा कॉल उचला.',
    goingOk: 'छान — बुकिंग In Progress झाली. सांभाळून जा.',
    reasonTitle: 'का नको?',
    r_not_available: 'आत्ता मोकळा नाही',
    r_on_leave_today: 'आज सुट्टीवर आहे',
    r_other: 'दुसरं कारण',
    r_far_from_me: 'खूप लांब आहे',
    r_service_type_different: 'ही सर्व्हिस मी करत नाही',
    r_time_not_preferred: 'ही वेळ जमणार नाही',
    gone: 'ही बुकिंग आता नाही — दुसऱ्या मेकॅनिककडे गेली.',
    closed: 'ही ऑफर बंद झाली. तुमच्या बुकिंग बघा.',
    error: 'पाठवता आलं नाही — नेटवर्क बघा',
    retry: 'पुन्हा पाठवा',
    ok: 'ठीक आहे',
    queue: '{n} पैकी 1',
    lblVehicle: 'गाडी',
    lblService: 'सर्व्हिस',
    lblArea: 'एरिया',
    lblTime: 'वेळ',
    today: 'आज',
    tomorrow: 'उद्या',
  },
  bn: {
    titleAccept: 'নতুন বুকিং — নেবেন?',
    titleGoing: 'কাস্টমারের কাছে যাচ্ছেন?',
    yes: 'হ্যাঁ',
    no: 'না',
    going: 'যাচ্ছি',
    notGoing: 'যাচ্ছি না',
    timeLeft: 'বাকি সময়',
    calling: 'সময় শেষ — আমরা এখনই আপনাকে কল করছি। এই বুকিংটা রাখতে {num} থেকে আসা কলটা ধরুন।',
    missed: 'এই বুকিংটা মিস হয়ে গেল — আপনি উত্তর দেননি।',
    submitting: 'পাঠাচ্ছি…',
    accepted: 'বুকিং আপনার। আমরা এখনই কল করছি — {num} থেকে আসা কলটা ধরুন।',
    goingOk: 'দারুণ — বুকিং In Progress হয়েছে। সাবধানে যান।',
    reasonTitle: 'কেন না?',
    r_not_available: 'এখন ফাঁকা নেই',
    r_on_leave_today: 'আজ ছুটিতে আছি',
    r_other: 'অন্য কারণ',
    r_far_from_me: 'অনেক দূরে',
    r_service_type_different: 'এই সার্ভিস আমি করি না',
    r_time_not_preferred: 'এই সময়টা হবে না',
    gone: 'এই বুকিং আর নেই — অন্য মেকানিকের কাছে চলে গেছে।',
    closed: 'এই অফার বন্ধ হয়ে গেছে। আপনার বুকিং দেখুন।',
    error: 'পাঠানো গেল না — নেটওয়ার্ক দেখুন',
    retry: 'আবার পাঠান',
    ok: 'ঠিক আছে',
    queue: '{n}-এর মধ্যে 1',
    lblVehicle: 'গাড়ি',
    lblService: 'সার্ভিস',
    lblArea: 'এলাকা',
    lblTime: 'সময়',
    today: 'আজ',
    tomorrow: 'কাল',
  },
};

// ─── styles (inline only: portaled nodes escape the app's nested SCSS) ──────
const RED = '#f42222';
const GREEN = '#1a9c4b';
const INK = '#1a1a1a';
const MUTED = '#6b7280';
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", "Helvetica Neue", Arial, sans-serif';

const btnBase = {
  display: 'block', width: '100%', boxSizing: 'border-box', margin: 0,
  padding: '12px 16px', borderRadius: '14px', fontFamily: FONT, fontWeight: 800,
  lineHeight: 1.3, textAlign: 'center', textTransform: 'none', letterSpacing: 'normal',
  cursor: 'pointer', outline: 'none', WebkitAppearance: 'none', appearance: 'none',
  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  userSelect: 'none', WebkitUserSelect: 'none',
};

const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.78)', zIndex: 100001,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: FONT, WebkitTextSizeAdjust: '100%',
  },
  card: {
    background: '#fff', borderRadius: '16px', width: 'calc(100% - 32px)', maxWidth: '440px',
    maxHeight: '94vh', overflowX: 'hidden', overflowY: 'auto', boxSizing: 'border-box',
    boxShadow: '0 10px 40px rgba(0,0,0,0.45)', textAlign: 'center', color: INK,
  },
  header: {
    background: RED, color: '#fff', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', padding: '14px 18px',
  },
  headerLeft: { display: 'flex', alignItems: 'center' },
  brand: { marginLeft: '10px', fontSize: '17px', fontWeight: 800, color: '#fff', lineHeight: 1.2 },
  queue: {
    background: 'rgba(255,255,255,0.24)', color: '#fff', borderRadius: '12px',
    padding: '4px 10px', fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap',
  },
  body: { padding: '22px 20px 24px' },
  title: { margin: '0 0 16px', padding: 0, fontFamily: FONT, fontSize: '24px', fontWeight: 800, color: INK, lineHeight: 1.35 },
  summary: {
    background: '#f6f7f9', borderRadius: '12px', padding: '4px 14px',
    margin: '0 0 18px', textAlign: 'left',
  },
  row: { display: 'flex', alignItems: 'baseline', padding: '9px 0' },
  rowLine: { borderTop: '1px solid #e4e7ec' },
  rowLabel: { flex: '0 0 84px', fontSize: '14px', fontWeight: 600, color: MUTED, lineHeight: 1.35 },
  rowValue: {
    flex: '1 1 auto', minWidth: 0, fontSize: '18px', fontWeight: 700, color: INK,
    lineHeight: 1.35, wordBreak: 'break-word',
  },
  timerWrap: { margin: '0 0 18px' },
  timerLabel: { fontSize: '14px', fontWeight: 600, color: MUTED, lineHeight: 1.3 },
  timer: (urgent) => ({
    fontSize: '56px', fontWeight: 800, lineHeight: 1.1, letterSpacing: '1px',
    fontVariantNumeric: 'tabular-nums', color: urgent ? RED : INK,
  }),
  message: { margin: '0 0 20px', padding: 0, fontFamily: FONT, fontSize: '20px', fontWeight: 600, color: INK, lineHeight: 1.5 },
  num: { whiteSpace: 'nowrap', fontWeight: 800, color: RED },
  icon: { margin: '2px 0 14px', lineHeight: 0 },
  yesBtn: (off) => ({
    ...btnBase, minHeight: '64px', fontSize: '24px', background: GREEN, color: '#fff',
    border: '2px solid ' + GREEN, opacity: off ? 0.45 : 1, cursor: off ? 'default' : 'pointer',
  }),
  noBtn: (off) => ({
    ...btnBase, minHeight: '64px', fontSize: '24px', background: '#fff', color: RED,
    border: '2px solid ' + RED, marginTop: '16px', opacity: off ? 0.45 : 1,
    cursor: off ? 'default' : 'pointer',
  }),
  sending: { margin: '14px 0 0', fontSize: '18px', fontWeight: 600, color: MUTED, lineHeight: 1.4 },
  errorBox: {
    background: '#fde8e8', color: '#b91c1c', borderRadius: '12px', padding: '12px 14px',
    margin: '0 0 14px', fontSize: '18px', fontWeight: 700, lineHeight: 1.4,
  },
  choice: (yes) => ({
    display: 'inline-block', margin: '0 0 14px', padding: '6px 18px', borderRadius: '20px',
    fontSize: '18px', fontWeight: 800, lineHeight: 1.3,
    background: yes ? GREEN : '#fff', color: yes ? '#fff' : RED,
    border: '2px solid ' + (yes ? GREEN : RED),
  }),
  retryBtn: {
    ...btnBase, minHeight: '64px', fontSize: '22px', background: RED, color: '#fff',
    border: '2px solid ' + RED,
  },
  okBtn: {
    ...btnBase, minHeight: '56px', fontSize: '20px', background: INK, color: '#fff',
    border: '2px solid ' + INK,
  },
  reasonBtn: (off, first) => ({
    ...btnBase, minHeight: '56px', fontSize: '19px', fontWeight: 700, background: '#fff',
    color: INK, border: '2px solid #d4d8de', marginTop: first ? 0 : '12px',
    opacity: off ? 0.45 : 1, cursor: off ? 'default' : 'pointer',
  }),
};

// ─── helpers (pure; no browser globals) ─────────────────────────────────────
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const clean = (v) => (v == null ? '' : String(v).trim());

const keyOf = (d) => (d ? String(d.offer_id) + '|' + d.kind : '');

const sameOffer = (a, b) => !!a && !!b && keyOf(a) === keyOf(b);

const isWindowed = (d) => !!d && d.kind === 'accept' && d.mode === 'window' && d.expires_at > 0;

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

const readToken = () => {
  try {
    return clean(getLocalItem('authToken'));
  } catch (e) {
    return '';
  }
};

const bearer = (token) => ({ Authorization: 'Bearer ' + token });

const normalize = (d) => {
  if (!d || d.offer_id == null || d.offer_id === '') return null;
  if (d.kind !== 'accept' && d.kind !== 'going') return null;
  const grace = num(d.call_grace_ms);
  return {
    offer_id: d.offer_id,
    kind: d.kind,
    booking_id: d.booking_id,
    mode: d.mode,
    expires_at: num(d.expires_at),
    call_grace_ms: grace > 0 ? grace : DEFAULT_CALL_GRACE_MS,
    summary: d.summary && typeof d.summary === 'object' ? d.summary : {},
  };
};

// "01205232888" -> "0120-523-2888"
const fmtCaller = (raw) => {
  const s = clean(raw) || DEFAULT_CALLER_ID;
  const digits = s.replace(/\D/g, '');
  if (digits.length === 11 && digits.charAt(0) === '0') {
    return digits.slice(0, 4) + '-' + digits.slice(4, 7) + '-' + digits.slice(7);
  }
  return s;
};

// "13:00:00" -> "1:00 PM"
const fmtTime = (raw) => {
  const s = clean(raw);
  if (!s || /[ap]\.?m/i.test(s)) return s;
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return s;
  const h24 = parseInt(m[1], 10);
  if (h24 > 23) return s;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return h12 + ':' + m[2] + (h24 >= 12 ? ' PM' : ' AM');
};

const istDay = (ms) => {
  try {
    return new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);
  } catch (e) {
    return '';
  }
};

// "2026-07-21" -> Today / Tomorrow / "21/07/2026"; anything else is shown as sent.
const fmtDate = (raw, nowMs, todayWord, tomorrowWord) => {
  const s = clean(raw);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  const iso = m[1] + '-' + m[2] + '-' + m[3];
  if (iso === istDay(nowMs)) return todayWord;
  if (iso === istDay(nowMs + 86400000)) return tomorrowWord;
  return m[3] + '/' + m[2] + '/' + m[1];
};

const fmtCountdown = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return (mm < 10 ? '0' : '') + mm + ':' + (ss < 10 ? '0' : '') + ss;
};

const Bell = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" focusable="false">
    <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zm7-6v-5c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C8.64 5.36 7 7.92 7 11v5l-2 2v1h14v-1l-2-2z" />
  </svg>
);

const Mark = ({ ok }) => (
  <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
    <circle cx="32" cy="32" r="30" fill={ok ? GREEN : '#9aa1ac'} />
    {ok ? (
      <path d="M19 33l9 9 17-19" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M32 17v20M32 46v1" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" />
    )}
  </svg>
);

class GateInner extends Component {
  constructor(props) {
    super(props);
    const machine = {
      current: null,          // offer on screen (normalized)
      phase: null,
      total: 0,               // open offers in the last poll -> "1 of N"
      pendingDecision: null,  // 'yes' | 'no' being (re)sent
      reasonSending: false,
      neutral: false,         // GONE with unknown outcome -> neutral wording
      vanished: false,        // GONE because the poll dropped it (not a server verdict)
      callerId: DEFAULT_CALLER_ID,
    };
    this.state = { mounted: false, lang: 'hi', nowTick: 0, ...machine };
    // Synchronous mirror of the machine fields: timers, poll and axios callbacks
    // read this, never this.state (setState is async). Written only via apply().
    this.m = { ...machine };

    this.alive = false;
    this.offset = 0;            // server_now - Date.now()
    this.inFlight = false;
    this.pollTimer = null;
    this.tickTimer = null;
    this.phaseTimer = null;
    this.lastPollStartAt = 0;
    this.lastPollOkAt = 0;
    this.pollFails = 0;
    this.lastDecisions = [];
    this.dismissed = {};        // offers finished locally -> never re-open
    this.shown = {};            // gate_shown fired
    this.serverFails = {};
    this.langRequested = false;
    this.armAt = 0;
    this.graceKickAt = 0;
    this.missCount = 0;         // consecutive good polls without the offer on screen
    this.hiddenNow = false;     // page hidden (he is in another app / on a call)
    this.lastHiddenEnd = 0;     // server-clock time the page last came back
    this.hardened = false;
    this.prevOverflow = '';
    this.navigating = false;
  }

  componentDidMount() {
    this.alive = true;
    this.hiddenNow = isHidden();
    this.setState({ mounted: true });
    document.addEventListener('visibilitychange', this.onWake);
    window.addEventListener('focus', this.onWake);
    this.poll();
  }

  componentWillUnmount() {
    this.alive = false;
    document.removeEventListener('visibilitychange', this.onWake);
    window.removeEventListener('focus', this.onWake);
    if (this.pollTimer) clearTimeout(this.pollTimer);
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.phaseTimer) clearTimeout(this.phaseTimer);
    this.pollTimer = null;
    this.tickTimer = null;
    this.phaseTimer = null;
    this.unlock();
  }

  now = () => Date.now() + this.offset;

  t = (key) => {
    const own = STR[this.state.lang];
    return (own && own[key]) || STR.hi[key] || STR.en[key] || '';
  };

  apply = (patch) => {
    if (!this.alive) return;
    Object.assign(this.m, patch);
    this.setState(patch);
    this.syncTick();
    if (this.m.current && this.m.phase) this.lock();
    else this.unlock();
  };

  // ─── polling ──────────────────────────────────────────────────────────────
  onWake = () => {
    if (!this.alive) return;
    if (isHidden()) {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.hiddenNow = true;
      return;
    }
    if (this.hiddenNow) {
      this.hiddenNow = false;
      this.lastHiddenEnd = this.now();
    }
    if (Date.now() - this.lastPollStartAt < WAKE_THROTTLE_MS) {
      if (!this.pollTimer && !this.inFlight) this.schedule();
      return;
    }
    this.poll();
  };

  schedule = () => {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    if (!this.alive || isHidden()) return;
    const every = this.m.current ? POLL_OPEN_MS : POLL_IDLE_MS;
    const wait = Math.max(TICK_MS, every - (Date.now() - this.lastPollStartAt));
    this.pollTimer = setTimeout(this.poll, wait);
  };

  poll = () => {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    if (!this.alive) return;
    if (this.inFlight) return;   // never overlap; the in-flight request reschedules
    if (isHidden()) return;      // never poll while hidden; onWake resumes
    this.lastPollStartAt = Date.now();
    const token = readToken();
    if (!token) {                // logged out: show nothing, send nothing
      this.hideAll();
      this.schedule();
      return;
    }
    this.ensureLang(token);
    this.inFlight = true;
    axios
      .get(API + '/api/v2/mechanic/pending_decisions', { headers: bearer(token), timeout: POLL_TIMEOUT_MS })
      .then(
        (res) => {
          this.inFlight = false;
          if (!this.alive) return;
          try {
            this.onPollOk(res && res.data);
          } catch (e) {
            console.warn('[DecisionGate] poll handler error', e && e.message);
            this.hideAll();
          }
        },
        (err) => {
          this.inFlight = false;
          if (!this.alive) return;
          this.onPollFail(err);
        }
      )
      .then(this.schedule, this.schedule);
  };

  ensureLang = (token) => {
    if (this.langRequested) return;
    this.langRequested = true;
    let phone = '';
    try {
      phone = clean(getLocalItem('mechanic_phone'));
    } catch (e) { /* noop */ }
    axios
      .get(API + '/api/mechanic_wizard_config', {
        params: phone ? { mechanic_phone: phone } : {},
        headers: bearer(token),
        timeout: POLL_TIMEOUT_MS,
      })
      .then((res) => {
        const lang = res && res.data && res.data.language;
        if (this.alive && lang && STR[lang]) this.setState({ lang });
      })
      .catch(() => { /* stays 'hi' */ });
  };

  onPollOk = (data) => {
    if (!data || !Array.isArray(data.decisions)) {
      this.onPollFail(new Error('bad payload'));
      return;
    }
    const serverNow = num(data.server_now);
    if (serverNow > 0) this.offset = serverNow - Date.now();
    this.pollFails = 0;
    this.lastPollOkAt = Date.now();

    const list = data.decisions.map(normalize).filter((d) => d && !this.dismissed[keyOf(d)]);
    this.lastDecisions = list;

    const callerId = clean(data.caller_id);
    if (callerId && callerId !== this.m.callerId) this.apply({ callerId });

    const { phase, current } = this.m;
    if (!current) {
      if (list.length) this.openOffer(list[0], list.length);
      return;
    }
    // SUBMITTING and the closing states finish on their own.
    if (phase !== OPEN && phase !== CALLING && phase !== ERROR) return;

    const pastGrace = phase === CALLING && this.now() >= current.expires_at + current.call_grace_ms;
    const fresh = list.filter((d) => sameOffer(d, current))[0];
    if (!fresh) {
      // Decided elsewhere, expired or reassigned. One empty answer can also be a
      // server hiccup, so it takes two in a row (one is enough once grace is over).
      this.missCount += 1;
      if (this.missCount < VANISH_CONFIRM_POLLS && !pastGrace) return;
      // "Went to another mechanic" is only a fair guess for an untouched accept
      // offer. During the call he may have just kept it; a going prompt closes on
      // any status change. Unknown outcome -> neutral wording.
      this.enterPhase(GONE, { vanished: true, neutral: phase === CALLING || current.kind === 'going' });
      return;
    }
    this.missCount = 0;
    this.apply({ current: fresh, total: list.length });
    if (pastGrace) this.enterPhase(MISSED);   // server still lists it: nobody picked up
  };

  onPollFail = (err) => {
    this.pollFails += 1;
    if (this.pollFails === 1) console.warn('[DecisionGate] poll failed', err && err.message);
    const { phase, current } = this.m;
    if (!current) return;
    if (phase === OPEN) {
      if (this.pollFails >= POLL_FAILS_BEFORE_HIDE) this.hideAll();
    } else if (phase === ERROR) {
      if (Date.now() - this.lastPollOkAt >= ERROR_OFFLINE_CLOSE_MS) this.hideAll();
    }
    // CALLING is bounded by its own clock (tick); the closing states by their timers.
  };

  // ─── state machine ────────────────────────────────────────────────────────
  clearPhaseTimer = () => {
    if (this.phaseTimer) clearTimeout(this.phaseTimer);
    this.phaseTimer = null;
  };

  enterPhase = (phase, patch) => {
    if (!this.alive) return;
    this.clearPhaseTimer();
    if (phase === OPEN || phase === REASON) this.armAt = Date.now() + ARM_MS;
    if (phase === CALLING) this.graceKickAt = 0;
    this.apply({ ...(patch || {}), phase });
    const ms = AUTO_CLOSE_MS[phase];
    if (ms) {
      const key = keyOf(this.m.current);
      this.phaseTimer = setTimeout(() => this.onPhaseTimeout(phase, key), ms);
    }
  };

  onPhaseTimeout = (phase, key) => {
    this.phaseTimer = null;
    if (!this.alive || this.m.phase !== phase || keyOf(this.m.current) !== key) return;
    if (phase === REASON) this.sendReason('other', true);
    else if (phase === ACCEPTED) this.finishAccepted();
    else this.closeCurrent();
  };

  openOffer = (offer, total) => {
    const late = isWindowed(offer) && this.now() >= offer.expires_at;
    this.missCount = 0;
    this.enterPhase(late ? CALLING : OPEN, { ...CLEARED, current: offer, total });
    this.fireShown(offer);
  };

  // Fail-open: take the gate down without marking the offer as finished, so it
  // comes back if the server still lists it on a later successful poll.
  hideAll = () => {
    this.clearPhaseTimer();
    this.lastDecisions = [];
    if (!this.m.current && !this.m.phase) return;
    this.apply(CLEARED);
  };

  closeCurrent = (leaving) => {
    this.clearPhaseTimer();
    // A vanished offer is not marked finished: if the server lists it again, it is open again.
    if (this.m.current && !this.m.vanished) this.dismissed[keyOf(this.m.current)] = true;
    const now = this.now();
    const rest = leaving ? [] : this.lastDecisions.filter((d) => (
      !this.dismissed[keyOf(d)] && !(isWindowed(d) && now >= d.expires_at + d.call_grace_ms)
    ));
    this.lastDecisions = rest;
    if (rest.length) {
      this.openOffer(rest[0], rest.length);
    } else {
      this.apply(CLEARED);
    }
    if (!leaving) this.poll();
  };

  finishAccepted = () => {
    const cur = this.m.current;
    let target = '';
    if (cur && cur.booking_id != null && cur.booking_id !== '') {
      target = '/mechanic-dashboard/' + encodeURIComponent(String(cur.booking_id));
    }
    let here = '';
    try {
      here = window.location.pathname.replace(/\/+$/, '');
    } catch (e) { /* noop */ }
    if (!target || here === target) {
      this.closeCurrent();
      return;
    }
    this.navigating = true;      // unlock() must not history.back() under a navigation
    this.closeCurrent(true);
    try {
      window.location.assign(target);
    } catch (e) {
      this.navigating = false;
      this.schedule();
    }
  };

  syncTick = () => {
    const { phase, current } = this.m;
    const need = this.alive && isWindowed(current)
      && (phase === OPEN || phase === SUBMITTING || phase === CALLING || phase === ERROR);
    if (need && !this.tickTimer) {
      this.tickTimer = setInterval(this.tick, TICK_MS);
    } else if (!need && this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  };

  tick = () => {
    if (!this.alive) return;
    const { phase, current } = this.m;
    if (!isWindowed(current)) return;
    const now = this.now();
    if (phase === SUBMITTING) {   // the server decides; only keep the clock moving
      this.setState({ nowTick: now });
      return;
    }
    if (phase === OPEN || phase === ERROR) {
      if (now >= current.expires_at) this.enterPhase(CALLING, { pendingDecision: null });
      else this.setState({ nowTick: now });
      return;
    }
    if (phase !== CALLING || now < current.expires_at + current.call_grace_ms) return;
    // Call grace is over. Polls never run while hidden, so decide once visible.
    if (isHidden()) return;
    // On screen for the whole call window with the server still listing the
    // offer: he did not pick up.
    const away = this.hiddenNow || this.lastHiddenEnd >= current.expires_at;
    if (!away && this.missCount === 0) {
      this.enterPhase(MISSED);
      return;
    }
    // Otherwise the page was hidden during the call window (normally: he was on
    // that call and may have kept the booking). Never say "you did not respond"
    // on a guess: one fresh poll decides (still listed -> MISSED, dropped ->
    // neutral "closed").
    if (!this.graceKickAt) {
      this.graceKickAt = Date.now();
      this.poll();
    } else if (Date.now() - this.graceKickAt >= GRACE_VERIFY_MS) {
      this.enterPhase(GONE, { vanished: true, neutral: true });
    }
  };

  // ─── actions ──────────────────────────────────────────────────────────────
  fireShown = (offer) => {
    const id = String(offer.offer_id);
    if (this.shown[id]) return;
    this.shown[id] = true;
    const token = readToken();
    if (!token) return;
    axios
      .post(API + '/api/v2/mechanic/offer_event', { offer_id: offer.offer_id, event: 'gate_shown' },
        { headers: bearer(token), timeout: POLL_TIMEOUT_MS })
      .catch(() => { /* best effort */ });
  };

  decide = (decision, isRetry) => {
    const { phase, current } = this.m;
    if (!current) return;
    if (isRetry ? phase !== ERROR : phase !== OPEN) return;   // also drops taps while SUBMITTING
    if (!isRetry && Date.now() < this.armAt) return;
    const token = readToken();
    if (!token) {
      this.hideAll();
      return;
    }
    const offer = current;
    this.enterPhase(SUBMITTING, { pendingDecision: decision });
    axios
      .post(API + '/api/v2/mechanic/offer_decision',
        { offer_id: offer.offer_id, booking_id: offer.booking_id, decision, via: 'web_gate' },
        { headers: bearer(token), timeout: SEND_TIMEOUT_MS })
      .then(
        (res) => this.onDecisionResult(offer, decision, res && res.data),
        (err) => this.onSendFail(offer, decision, (err && err.response && err.response.status) || 0,
          err && err.message)
      );
  };

  onYes = () => this.decide('yes', false);

  onNo = () => this.decide('no', false);

  onRetry = () => {
    if (this.m.pendingDecision) this.decide(this.m.pendingDecision, true);
  };

  onOk = () => {
    const { phase } = this.m;
    if (phase === MISSED || phase === GONE) this.closeCurrent();
  };

  stillSending = (offer) => this.alive && this.m.phase === SUBMITTING && sameOffer(this.m.current, offer);

  onDecisionResult = (offer, decision, data) => {
    if (!this.stillSending(offer)) return;
    const result = data && data.result;
    const callerId = clean(data && data.caller_id) || this.m.callerId;
    if (result === 'accepted' || result === 'rejected' || result === 'going' || result === 'not_going') {
      this.notifyNative(offer, result);
    }
    if (result === 'accepted') {
      this.enterPhase(ACCEPTED, { callerId });
    } else if (result === 'going') {
      this.enterPhase(GOING);
    } else if (result === 'rejected' || result === 'not_going') {
      this.enterPhase(REASON, { reasonSending: false });
    } else if (result === 'already_decided') {
      // The server names the recorded decision when it has one; trust it over this tap.
      const recorded = clean(data && data.decision);
      const wasYes = recorded ? (recorded === 'accepted' || recorded === 'going') : decision === 'yes';
      if (wasYes) this.enterPhase(offer.kind === 'going' ? GOING : ACCEPTED, { callerId });
      else this.enterPhase(GONE, { neutral: true });
    } else if (result === 'expired' || result === 'reassigned') {
      this.enterPhase(GONE, { neutral: false });
    } else {
      this.onSendFail(offer, decision, 200, 'unknown result ' + String(result));
    }
  };

  // status 0 = no HTTP answer (network / timeout).
  onSendFail = (offer, decision, status, message) => {
    if (!this.stillSending(offer)) return;
    console.warn('[DecisionGate] decision not sent', status, message);
    if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
      this.closeCurrent();       // request refused: a retry cannot succeed -> never trap
      return;
    }
    if (status) {
      const key = keyOf(offer);
      this.serverFails[key] = (this.serverFails[key] || 0) + 1;
      if (this.serverFails[key] >= MAX_SERVER_FAILS) {
        this.closeCurrent();
        return;
      }
    }
    this.enterPhase(ERROR, { pendingDecision: decision });
  };

  // The NO is already recorded; this only collects why. Never traps: closes when
  // the POST settles (either way), and auto-sends 'other' after 30 s.
  sendReason = (code, auto) => {
    const { phase, current, reasonSending } = this.m;
    if (phase !== REASON || !current || reasonSending) return;
    if (!auto && Date.now() < this.armAt) return;
    const offer = current;
    const done = () => {
      if (this.alive && this.m.phase === REASON && sameOffer(this.m.current, offer)) this.closeCurrent();
    };
    this.clearPhaseTimer();
    this.apply({ reasonSending: true });
    const token = readToken();
    if (!token) {
      done();
      return;
    }
    const payload = { offer_id: offer.offer_id, reason_code: code };
    if (auto) payload.reason_text = 'auto_timeout';
    axios
      .post(API + '/api/v2/mechanic/offer_reason', payload, { headers: bearer(token), timeout: REASON_TIMEOUT_MS })
      .then(done, (err) => {
        console.warn('[DecisionGate] reason not sent', err && err.message);
        done();
      });
    if (auto) done();
  };

  // Must NOT carry a top-level `mechanic_phone` or `authToken` key: the native
  // app treats any message with those keys as an auth relay.
  notifyNative = (offer, result) => {
    try {
      const bridge = typeof window !== 'undefined' ? window.ReactNativeWebView : null;
      if (bridge && typeof bridge.postMessage === 'function') {
        bridge.postMessage(JSON.stringify({
          type: 'BOOKING_DECISION_RESOLVED',
          booking_id: offer.booking_id,
          offer_id: offer.offer_id,
          decision: result,
        }));
      }
    } catch (e) { /* noop */ }
  };

  // ─── hardening while visible: scroll lock + back trap ─────────────────────
  lock = () => {
    if (this.hardened || typeof document === 'undefined') return;
    this.hardened = true;
    try {
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } catch (e) { /* noop */ }
    try {
      window.addEventListener('popstate', this.onPopState);
      this.pushTrap();
    } catch (e) { /* noop */ }
  };

  unlock = () => {
    if (!this.hardened) return;
    this.hardened = false;
    try {
      document.body.style.overflow = this.prevOverflow || '';
    } catch (e) { /* noop */ }
    try {
      window.removeEventListener('popstate', this.onPopState);
      const st = window.history.state;
      if (!this.navigating && st && st[TRAP_KEY]) window.history.back();
    } catch (e) { /* noop */ }
  };

  // The trap entry keeps a copy of the router's own history.state ({key,state}):
  // mechanic-dashboard reads location.state on mount, so a reload while the trap
  // entry is on top must still find it.
  pushTrap = () => {
    const st = window.history.state;
    if (st && st[TRAP_KEY]) return;
    const next = { ...(st && typeof st === 'object' ? st : {}), [TRAP_KEY]: true };
    window.history.pushState(next, '', window.location.href);
  };

  onPopState = () => {
    if (!this.hardened) return;
    try {
      this.pushTrap();
    } catch (e) { /* noop */ }
  };

  // ─── render ───────────────────────────────────────────────────────────────
  withNum = (template, callerId) => {
    const parts = String(template).split('{num}');
    if (parts.length < 2) return template;
    return (
      <>
        {parts[0]}
        <span style={S.num}>{fmtCaller(callerId)}</span>
        {parts.slice(1).join('')}
      </>
    );
  };

  renderSummary = (offer) => {
    const s = offer.summary || {};
    const date = fmtDate(s.slot_date, this.now(), this.t('today'), this.t('tomorrow'));
    const time = fmtTime(s.slot_time);
    const rows = [
      [this.t('lblVehicle'), clean(s.vehicle)],
      [this.t('lblService'), clean(s.service)],
      [this.t('lblArea'), clean(s.locality)],
      [this.t('lblTime'), [date, time].filter(Boolean).join(', ')],
    ].filter((r) => r[1]);
    if (!rows.length) return null;
    return (
      <div style={S.summary}>
        {rows.map((r, i) => (
          <div key={r[0]} style={i ? { ...S.row, ...S.rowLine } : S.row}>
            <span style={S.rowLabel}>{r[0]}</span>
            <span style={S.rowValue}>{r[1]}</span>
          </div>
        ))}
      </div>
    );
  };

  renderDecision = (offer, phase) => {
    const { pendingDecision } = this.state;
    const isGoing = offer.kind === 'going';
    const yesLabel = this.t(isGoing ? 'going' : 'yes');
    const noLabel = this.t(isGoing ? 'notGoing' : 'no');
    const left = isWindowed(offer) ? offer.expires_at - this.now() : 0;
    const busy = phase === SUBMITTING;
    return (
      <>
        <h2 style={S.title}>{this.t(isGoing ? 'titleGoing' : 'titleAccept')}</h2>
        {this.renderSummary(offer)}
        {isWindowed(offer) && phase !== CALLING ? (
          <div style={S.timerWrap}>
            <div style={S.timerLabel}>{this.t('timeLeft')}</div>
            <div style={S.timer(left <= 15000)}>{fmtCountdown(left)}</div>
          </div>
        ) : null}
        {phase === CALLING ? (
          <p style={{ ...S.message, marginBottom: 0 }} aria-live="assertive">
            {this.withNum(this.t('calling'), this.state.callerId)}
          </p>
        ) : null}
        {phase === ERROR ? (
          <>
            <div style={S.errorBox} role="alert">{this.t('error')}</div>
            <div><span style={S.choice(pendingDecision === 'yes')}>{pendingDecision === 'yes' ? yesLabel : noLabel}</span></div>
            <button type="button" style={S.retryBtn} onClick={this.onRetry}>{this.t('retry')}</button>
          </>
        ) : null}
        {phase === OPEN || phase === SUBMITTING ? (
          <>
            <button type="button" style={S.yesBtn(busy)} disabled={busy} onClick={this.onYes}>{yesLabel}</button>
            <button type="button" style={S.noBtn(busy)} disabled={busy} onClick={this.onNo}>{noLabel}</button>
            {busy ? <p style={S.sending} aria-live="polite">{this.t('submitting')}</p> : null}
          </>
        ) : null}
      </>
    );
  };

  renderNotice = (ok, text, withOk) => (
    <>
      <div style={S.icon}><Mark ok={ok} /></div>
      <p style={withOk ? S.message : { ...S.message, marginBottom: 0 }} aria-live="assertive">{text}</p>
      {withOk ? <button type="button" style={S.okBtn} onClick={this.onOk}>{this.t('ok')}</button> : null}
    </>
  );

  renderReason = (offer) => {
    const off = this.state.reasonSending;
    const codes = REASONS[offer.kind] || REASONS.accept;
    return (
      <>
        <h2 style={S.title}>{this.t('reasonTitle')}</h2>
        {codes.map((code, i) => (
          <button
            key={code}
            type="button"
            style={S.reasonBtn(off, i === 0)}
            disabled={off}
            onClick={() => this.sendReason(code, false)}
          >
            {this.t('r_' + code)}
          </button>
        ))}
        {off ? <p style={S.sending} aria-live="polite">{this.t('submitting')}</p> : null}
      </>
    );
  };

  renderBody = (offer, phase) => {
    if (phase === MISSED) return this.renderNotice(false, this.t('missed'), true);
    if (phase === GONE) return this.renderNotice(false, this.t(this.state.neutral ? 'closed' : 'gone'), true);
    if (phase === ACCEPTED) return this.renderNotice(true, this.withNum(this.t('accepted'), this.state.callerId), false);
    if (phase === GOING) return this.renderNotice(true, this.t('goingOk'), false);
    if (phase === REASON) return this.renderReason(offer);
    return this.renderDecision(offer, phase);
  };

  render() {
    const { mounted, current, phase, total } = this.state;
    if (!mounted || !current || !phase) return null;
    if (typeof document === 'undefined' || !document.body) return null;
    const showQueue = total > 1 && (phase === OPEN || phase === SUBMITTING || phase === ERROR || phase === CALLING);
    // No close button and no overlay handler: the only ways out are the buttons
    // below and the timers above.
    const gate = (
      <div style={S.overlay}>
        <div style={S.card} role="dialog" aria-modal="true">
          <div style={S.header}>
            <div style={S.headerLeft}>
              <Bell />
              <span style={S.brand}>Ride N Repair</span>
            </div>
            {showQueue ? <span style={S.queue}>{this.t('queue').replace('{n}', String(total))}</span> : null}
          </div>
          <div style={S.body}>{this.renderBody(current, phase)}</div>
        </div>
      </div>
    );
    return ReactDOM.createPortal(gate, document.body);
  }
}

// Error boundary shell: a bug inside the gate must never take the mechanic's
// page down with it (React 17 unmounts the whole tree on an uncaught error).
class BookingDecisionGate extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('[DecisionGate] disabled after error', error && error.message);
  }

  render() {
    if (this.state.failed) return null;
    return <GateInner />;
  }
}

export default BookingDecisionGate;
